/**
 * Storage Adapter Factory
 * Selects and initializes the appropriate storage adapter based on runtime environment
 */

import type { IStorageAdapter } from './types';
import { BrowserStorageAdapter } from './BrowserStorageAdapter';
import { EnhancedBrowserStorageAdapter } from './EnhancedBrowserStorageAdapter';
import { FreeTierStorageAdapter } from './FreeTierStorageAdapter';

let cachedAdapter: IStorageAdapter | null = null;

export async function getStorageAdapter(): Promise<IStorageAdapter> {
  if (cachedAdapter) {
    return cachedAdapter;
  }

  // Detect environment
  const hasWindow = typeof window !== 'undefined';
  const isTauri = hasWindow && '__TAURI__' in window;
  const isElectron = hasWindow && 'electron' in window;
  const isBrowser = hasWindow && !isTauri && !isElectron;

  // Check for enhanced adapter flag in localStorage
  const useEnhanced =
    hasWindow &&
    localStorage.getItem('phrasewright:useEnhancedStorage') === 'true';

  let adapter: IStorageAdapter;

  if (isTauri) {
    // TODO: Implement TauriStorageAdapter
    adapter = useEnhanced
      ? new EnhancedBrowserStorageAdapter()
      : new BrowserStorageAdapter();
  } else if (isElectron) {
    // TODO: Implement ElectronStorageAdapter
    adapter = useEnhanced
      ? new EnhancedBrowserStorageAdapter()
      : new BrowserStorageAdapter();
  } else if (isBrowser) {
    adapter = useEnhanced
      ? new EnhancedBrowserStorageAdapter()
      : new BrowserStorageAdapter();
  } else {
    throw new Error('Unsupported environment');
  }

  // Initialize the adapter
  await adapter.initialize();

  // Cache for future use
  cachedAdapter = adapter;

  return adapter;
}

export function clearAdapterCache(): void {
  if (cachedAdapter) {
    cachedAdapter.cleanup();
    cachedAdapter = null;
  }
}

export interface StorageAdapterOptions {
  tier?: 'free' | 'paid';
  useSessionStorage?: boolean;
  compressionEnabled?: boolean;
  useEnhancedAdapter?: boolean;
}

export async function getStorageAdapterWithOptions(
  options: StorageAdapterOptions = {}
): Promise<IStorageAdapter> {
  const { tier = 'free', useEnhancedAdapter = false } = options;

  let adapter: IStorageAdapter;

  if (tier === 'free') {
    adapter = new FreeTierStorageAdapter({
      useSessionStorage: options.useSessionStorage,
      compressionEnabled: options.compressionEnabled,
    });
  } else {
    // For paid tiers, use enhanced adapter if requested
    if (useEnhancedAdapter) {
      adapter = new EnhancedBrowserStorageAdapter();
    } else {
      adapter = new BrowserStorageAdapter();
    }
  }

  await adapter.initialize();
  return adapter;
}

export async function getFreeTierAdapter(
  useSessionStorage = false
): Promise<FreeTierStorageAdapter> {
  const adapter = new FreeTierStorageAdapter({ useSessionStorage });
  await adapter.initialize();
  return adapter;
}
